import { Grid } from "@mui/material";
import React, { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { Module, SubModule } from "../../models/Modules";
import { getValue } from "../../utils";
import UnAuthorizedPage from "../errorPages/401";
import ForbiddenPage from "../errorPages/403";

const normalizePath = (path: string): string => {
  if (!path) {
    return "/";
  }

  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }

  normalized = normalized.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};

const joinRoutePaths = (parent: string, child: string): string => {
  const parentNormalized = normalizePath(parent);
  const childNormalized = child.startsWith("/") ? child : `/${child}`;
  return normalizePath(`${parentNormalized}${childNormalized}`);
};

const routePatternMatches = (routePattern: string, currentPath: string): boolean => {
  const patternSegments = normalizePath(routePattern).split("/").filter(Boolean);
  const pathSegments = normalizePath(currentPath).split("/").filter(Boolean);

  if (patternSegments.length !== pathSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) => {
    if (segment.startsWith(":")) {
      return pathSegments[index].length > 0;
    }
    return segment === pathSegments[index];
  });
};

const routePatternMatchesPrefix = (routePattern: string, currentPath: string): boolean => {
  const patternSegments = normalizePath(routePattern).split("/").filter(Boolean);
  const pathSegments = normalizePath(currentPath).split("/").filter(Boolean);

  if (patternSegments.length === 0) {
    return currentPath === "/";
  }

  const comparableLength = Math.min(patternSegments.length, pathSegments.length);

  for (let index = 0; index < comparableLength; index += 1) {
    const segment = patternSegments[index];
    const pathSegment = pathSegments[index];

    if (segment.startsWith(":")) {
      if (!pathSegment || pathSegment.length === 0) {
        return false;
      }
      continue;
    }

    if (segment !== pathSegment) {
      return false;
    }
  }

  return comparableLength > 0;
};

const collectSubModuleRoutes = (
  baseRoute: string,
  subModules: SubModule[]
): string[] => {
  const collectedRoutes: string[] = [];

  subModules.forEach((subModule) => {
    const fullRoute = joinRoutePaths(baseRoute, subModule.routeUrl || "");
    collectedRoutes.push(fullRoute);

    if (subModule.subModules && subModule.subModules.length > 0) {
      collectedRoutes.push(...collectSubModuleRoutes(fullRoute, subModule.subModules));
    }
  });

  return collectedRoutes;
};

const isRouteAllowedForUser = (pathname: string, userModuleResponse: Module[]): boolean => {
  const allowedRoutePatterns: string[] = [];

  userModuleResponse.forEach((module) => {
    const moduleRoute = normalizePath(module.routeUrl || "/");
    allowedRoutePatterns.push(moduleRoute);

    if (module.subModules && module.subModules.length > 0) {
      allowedRoutePatterns.push(...collectSubModuleRoutes(moduleRoute, module.subModules));
    }
  });

  return allowedRoutePatterns.some((routePattern) =>
    routePatternMatches(routePattern, pathname)
  );
};

const AuthGuard = ({
  children,
  authenticated,
  userModuleResponse,
  requiredRoutePattern,
  knownRoutePatterns,
  fallbackElement,
}: {
  children: ReactNode;
  authenticated: boolean;
  userModuleResponse: Module[];
  requiredRoutePattern?: string;
  knownRoutePatterns?: string[];
  fallbackElement?: ReactNode;
}) => {
  const { pathname } = useLocation();
  const isValidRouteForUser = isRouteAllowedForUser(pathname, userModuleResponse);
  const hasValidModuleId = getValue(userModuleResponse, "[0].moduleId", -1) > -1;
  const normalizedRequiredRoutePattern = requiredRoutePattern
    ? normalizePath(requiredRoutePattern)
    : "";
  const isRestrictedSubModuleRoute =
    normalizedRequiredRoutePattern !== "" &&
    normalizedRequiredRoutePattern !== "/" &&
    routePatternMatches(normalizedRequiredRoutePattern, pathname);
  const matchesKnownProtectedRoute = (knownRoutePatterns || []).some(
    (routePattern) =>
      normalizePath(routePattern) !== "/" &&
      routePatternMatchesPrefix(routePattern, pathname)
  );
  const isFallbackRouteCheck = Boolean(fallbackElement);
  const shouldShowForbiddenPage =
    hasValidModuleId &&
    (isValidRouteForUser === false) &&
    (isRestrictedSubModuleRoute || matchesKnownProtectedRoute || isFallbackRouteCheck);
  const isAuthorized = authenticated === true && isValidRouteForUser;
  return isAuthorized ? (
    <Grid>{children}</Grid>
  ) : (
    <React.Fragment>
      {shouldShowForbiddenPage ? (
        <ForbiddenPage></ForbiddenPage>
      ) : fallbackElement ? (
        <React.Fragment>{fallbackElement}</React.Fragment>
      ) : hasValidModuleId ? (
        <UnAuthorizedPage></UnAuthorizedPage>
      ) : (
        ""
      )}
    </React.Fragment>
  );
};

export default AuthGuard;
